import { useState } from 'react'

import { cap } from '../../../State'
import { translations } from '../../../translations'
import { useComando } from './api'
import DialogConferma from './DialogConferma'
import {
  cambiaCanale,
  cambiaModalita,
  CANALI_NORA,
  CAPABILITY_IDS,
  CHIAVI_CANALE,
  CHIAVI_MEZZO,
  formatta,
  formDaCard,
  formUsaGoelett,
  goelettAttivo,
  MEZZI_TRIP_PLANNER,
  modalitaPerCanale,
  payloadSalvataggio,
  PROCESS_UNIT,
  puoSalvare,
  scrittureEsterne,
  testoCapability,
  testoModalita,
  usaAgentico,
  VIAGGI,
  viaggiDi,
  viaggiDisponibili
} from './Functions'
import Select from './Select'
import StatoCard from './StatoCard'

const language = cap.language.state || 'IT'
const t = translations[language] || translations.IT

/** Etichetta e descrizione di ogni scelta di viaggio. */
const CHIAVI_VIAGGI = {
  nessuno: { label: 'cnViaggiNessuno', description: 'cnViaggiNessunoDesc' },
  dinamico: { label: 'cnModoDynamicDemoBreve', description: 'cnModoDynamicDemoDesc' },
  goelett: { label: 'cnModoGoelettBookingBreve', description: 'cnModoGoelettBookingDesc' }
}

/**
 * Form «modalità + tre capabilities» con l'unica azione ordinaria
 * «Salva e applica». Serve sia al default environment
 * (variante `default`: due card separate) sia a un override community
 * (variante `community`: blocco compatto dentro la riga).
 *
 * Lo stato locale nasce dalla card: il genitore rimonta il componente
 * (prop `key`) quando cambia la revisione, così dopo un salvataggio o un
 * conflitto 409 il form riparte dallo stato del server.
 *
 * @param {Object} props - Proprietà
 * @param {Object} props.overview - DTO overview
 * @param {Object|null} props.card - Card salvata (null se non esiste)
 * @param {{ mode: string, capabilities: Object }} [props.formIniziale] - Form di partenza (default: dalla card)
 * @param {number|null} props.communityPresetId - null = default environment
 * @param {'default'|'community'} props.variante - Resa
 * @param {() => void} [props.onSalvato] - Dopo un salvataggio riuscito
 * @param {import('react').ReactNode} [props.azioniExtra] - Azioni accanto al salvataggio
 * @param {() => void} [props.onApriGoelett] - Apre e mostra il collegamento Goelett
 * @returns {import('react').JSX.Element}
 */
export const SchedaConfigurazione = ({
  overview,
  card,
  formIniziale,
  communityPresetId,
  variante,
  onSalvato,
  azioniExtra,
  onApriGoelett
}) => {
  const [form, setForm] = useState(() => formIniziale || formDaCard(card))
  const [conferma, setConferma] = useState(false)
  const { esegui, inCorso } = useComando()
  const perCommunity = communityPresetId !== null && communityPresetId !== undefined
  const idBase = perCommunity ? `cn-c${communityPresetId}` : 'cn-default'
  // Le capabilities servono i canali agentici: senza nessuno restano spente.
  const processUnit = !usaAgentico(form)
  // La conferma nomina la prima modalità con scritture esterne fra base e canali.
  const modalitaUsate = [form.mode, ...Object.values(form.canali || {})]
  const modalitaConScritture =
    modalitaUsate.find((m) => scrittureEsterne(m, overview, t)) ?? form.mode
  const scritture = scrittureEsterne(modalitaConScritture, overview, t)
  // Il server rifiuta Prenotazione Goelett senza collegamento attivo: lo si dice qui,
  // accanto alla scelta, invece di lasciar scoprire l'errore al salvataggio.
  const manca = formUsaGoelett(form) && !goelettAttivo(overview)

  const salva = async () => {
    const esito = await esegui(
      payloadSalvataggio({
        environmentId: overview.environment.id,
        communityPresetId: perCommunity ? communityPresetId : null,
        card,
        form
      }),
      { titolo: t.cnSalvato, dettaglio: t.cnSoloNuoveChat }
    )
    if (esito !== 'errore') setConferma(false)
    if (esito === 'ok') onSalvato?.()
  }

  const clickSalva = () => (scritture ? setConferma(true) : salva())

  // Due scelte invece di quattro modalità: il motore (Process-Unit o Nora) e, per Nora, i
  // viaggi. Il server tiene la modalità: Nora senza viaggi è `assistant`.
  const nora = form.mode !== PROCESS_UNIT
  const viaggi = viaggiDi(form.mode) ?? 'nessuno'
  const opzione = ({ id, nome, scelta, onScegli, titolo, descrizione, badge }) => (
    <label key={id} htmlFor={id} className={`cn-opzione${scelta ? ' is-on' : ''}`}>
      <input
        type="radio"
        className="cn-radio"
        id={id}
        name={nome}
        checked={scelta}
        onChange={onScegli}
      />
      <span className="cn-opzione__testo">
        <span className="cn-opzione__titolo">
          {titolo}
          {badge}
        </span>
        <span className="cn-opzione__desc">{descrizione}</span>
      </span>
    </label>
  )
  const motore = (
    <div role="radiogroup" aria-label={t.cnMotore} className="cn-scelte">
      {opzione({
        id: `${idBase}-motore-pu`,
        nome: `${idBase}-motore`,
        scelta: !nora,
        onScegli: () => setForm((old) => cambiaModalita(old, PROCESS_UNIT)),
        titolo: t.cnModoProcessUnit,
        descrizione: t.cnModoProcessUnitDesc
      })}
      {opzione({
        id: `${idBase}-motore-nora`,
        nome: `${idBase}-motore`,
        scelta: nora,
        // Si entra in Nora senza viaggi: cercare o prenotare è una scelta esplicita.
        onScegli: () => setForm((old) => cambiaModalita(old, VIAGGI.nessuno)),
        titolo: t.cnMotoreNora,
        descrizione: t.cnMotoreNoraDesc
      })}
    </div>
  )
  const sceltaViaggi = nora && (
    <div style={{ display: 'grid', gap: 8 }}>
      <h4 style={{ margin: '4px 0 0' }}>{t.cnViaggi}</h4>
      <div role="radiogroup" aria-label={t.cnViaggi} style={{ display: 'grid', gap: 8 }}>
        {viaggiDisponibili(overview.modes).map((v) => {
          const modo = (overview.modes || []).find((m) => m.id === VIAGGI[v])
          return opzione({
            id: `${idBase}-viaggi-${v}`,
            nome: `${idBase}-viaggi`,
            scelta: viaggi === v,
            onScegli: () => setForm((old) => cambiaModalita(old, VIAGGI[v])),
            titolo: t[CHIAVI_VIAGGI[v].label],
            descrizione: t[CHIAVI_VIAGGI[v].description],
            badge: modo?.writesExternalSystems ? (
              <span className="nbadge nbadge--warn">{t.cnScriveSistemiEsterni}</span>
            ) : null
          })
        })}
      </div>
    </div>
  )

  // 09 §3.6 — la modalità di ciascun canale: «come la predefinita» o una modalità che
  // serve quel canale. L'esito salvato (con la prontezza) viene dal server: `card.channels`.
  const effettivoSalvato = (canale) => (card?.channels || []).find((c) => c.id === canale)
  // Un canale servito da una sola modalità (oggi email, Midoffice, WhatsApp: Process-Unit)
  // non ha niente da scegliere: una riga di testo, non tre select con un'opzione sola.
  const canaliFissi = CANALI_NORA.filter(
    (canale) => modalitaPerCanale(canale, overview.modes).length <= 1 && !form.canali?.[canale]
  )
  const canali = (
    <div style={{ display: 'grid', gap: 10 }}>
      <span style={{ fontSize: 13, color: 'var(--ink-500)' }}>{t.cnCanaliHint}</span>
      {CANALI_NORA.filter((canale) => !canaliFissi.includes(canale)).map((canale) => {
        const id = `${idBase}-canale-${canale}`
        const salvato = effettivoSalvato(canale)
        return (
          <div key={canale} className="cn-canale">
            <label htmlFor={id} className="cn-canale__nome">
              {t[CHIAVI_CANALE[canale]] || canale}
            </label>
            <span style={{ display: 'grid', gap: 4, minWidth: 0 }}>
              <Select
                id={id}
                size="sm"
                value={form.canali?.[canale] || ''}
                onChange={(valore) => setForm((old) => cambiaCanale(old, canale, valore || null))}
              >
                <option value="">{t.cnCanaleComePredefinita}</option>
                {modalitaPerCanale(canale, overview.modes).map((m) => (
                  <option key={m.id} value={m.id}>
                    {testoModalita(m.id, overview.modes, t).label}
                  </option>
                ))}
              </Select>
              {salvato && (
                <span style={{ fontSize: 12, color: 'var(--ink-500)' }}>
                  {salvato.mode
                    ? formatta(t.cnCanaleOggi, {
                        modalita: testoModalita(salvato.mode, overview.modes, t).label
                      })
                    : t.cnCanaleNonAbilitato}
                  {salvato.reasons?.length ? ` — ${salvato.reasons.join('; ')}` : ''}
                </span>
              )}
            </span>
          </div>
        )
      })}
      {canaliFissi.length ? (
        <span style={{ fontSize: 12, color: 'var(--ink-500)' }}>
          {formatta(t.cnCanaliFissi, {
            canali: canaliFissi.map((canale) => t[CHIAVI_CANALE[canale]] || canale).join(', ')
          })}
        </span>
      ) : null}
    </div>
  )

  const cambiaMezzo = (mezzo, acceso) =>
    setForm((old) => {
      const correnti = old.mezzi ?? []
      const nuovi = acceso
        ? MEZZI_TRIP_PLANNER.filter((m) => correnti.includes(m) || m === mezzo)
        : correnti.filter((m) => m !== mezzo)
      // Nessun mezzo selezionato = nessuna scelta: si torna al default dell'installazione
      // invece di salvare un elenco vuoto, che il server rifiuta.
      return { ...old, mezzi: nuovi.length ? nuovi : null }
    })

  // D29 — i mezzi stanno **sotto** la capability che governano, e compaiono solo quando è
  // accesa: una scelta di mezzi con il planner spento non è salvabile. Stanno fuori dalla
  // label della capability: due label annidate non sono HTML valido.
  const mezzi = (
    <div style={{ display: 'grid', gap: 6, paddingLeft: 2 }}>
      <span style={{ fontSize: 13, fontWeight: 600 }}>{t.cnMezziTitolo}</span>
      <span style={{ fontSize: 12, color: 'var(--ink-500)' }}>{t.cnMezziAiuto}</span>
      <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 2 }}>
        {MEZZI_TRIP_PLANNER.map((mezzo) => {
          const idMezzo = `${idBase}-mezzo-${mezzo}`
          const acceso = Boolean(form.mezzi?.includes(mezzo))
          return (
            <label key={mezzo} htmlFor={idMezzo} className={`cn-pillola${acceso ? ' is-on' : ''}`}>
              <input
                type="checkbox"
                id={idMezzo}
                checked={acceso}
                onChange={(e) => cambiaMezzo(mezzo, e.target.checked)}
              />
              {t[CHIAVI_MEZZO[mezzo]]}
            </label>
          )
        })}
      </span>
      {!form.mezzi?.length && (
        <span style={{ fontSize: 12, color: 'var(--ink-500)' }}>{t.cnMezziDefault}</span>
      )}
    </div>
  )

  const capabilities = (
    <div style={{ display: 'grid', gap: 8 }}>
      {processUnit && (
        <span style={{ fontSize: 13, color: 'var(--ink-500)' }}>{t.cnCapabilitiesProcessUnit}</span>
      )}
      {!processUnit &&
        CAPABILITY_IDS.map((capId) => {
          const testo = testoCapability(capId, overview.capabilities, t)
          const id = `${idBase}-cap-${capId}`
          const acceso = form.capabilities[capId]
          return (
            <div
              key={capId}
              className={[
                'cn-opzione',
                'cn-opzione--col',
                acceso && !processUnit ? 'is-on' : '',
                processUnit ? 'is-disabled' : ''
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <label htmlFor={id} className="cn-opzione__riga">
                <span className="cn-opzione__testo">
                  <span className="cn-opzione__titolo">{testo.label}</span>
                  <span className="cn-opzione__desc">{testo.description}</span>
                </span>
                <input
                  type="checkbox"
                  role="switch"
                  className="cn-switch"
                  id={id}
                  checked={acceso}
                  disabled={processUnit}
                  onChange={(e) =>
                    setForm((old) => ({
                      ...old,
                      capabilities: { ...old.capabilities, [capId]: e.target.checked }
                    }))
                  }
                />
              </label>
              {capId === 'trip_planner' && acceso && !processUnit && mezzi}
            </div>
          )
        })}
    </div>
  )

  // I viaggi a più tappe: ogni volo con la sua tratta e un hotel per tappa. Non vale
  // con Goelett, che cerca un solo servizio per tipo: lì un giro resta una tratta alla volta.
  const idMultiTratta = `${idBase}-multi-tratta`
  const usaDinamico = [form.mode, ...Object.values(form.canali || {})].includes(VIAGGI.dinamico)
  const multiTratta = usaDinamico && (
    <div
      className={[
        'cn-opzione',
        'cn-opzione--col',
        form.multiTratta === true && !processUnit ? 'is-on' : '',
        processUnit ? 'is-disabled' : ''
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <label htmlFor={idMultiTratta} className="cn-opzione__riga">
        <span className="cn-opzione__testo">
          <span className="cn-opzione__titolo">{t.cnMultiTratta}</span>
          <span className="cn-opzione__desc">{t.cnMultiTrattaDesc}</span>
        </span>
        <input
          type="checkbox"
          role="switch"
          className="cn-switch"
          id={idMultiTratta}
          checked={form.multiTratta === true}
          disabled={processUnit}
          onChange={(e) => setForm((old) => ({ ...old, multiTratta: e.target.checked }))}
        />
      </label>
    </div>
  )

  const barraSalvataggio = (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <button
        type="button"
        className="nbtn nbtn--primary"
        onClick={clickSalva}
        disabled={inCorso || manca || !puoSalvare({ card, form, perCommunity })}
      >
        {t.cnSalvaEUsa}
      </button>
      {azioniExtra}
      <span style={{ fontSize: 12, color: manca ? 'var(--warning)' : 'var(--ink-500)' }}>
        {manca ? t.cnSalvaBloccatoGoelett : t.cnSoloNuoveChat}
      </span>
    </div>
  )

  const avvisoGoelett = manca && (
    <div
      role="status"
      className="nsec"
      style={{
        display: 'flex',
        gap: 10,
        alignItems: 'center',
        flexWrap: 'wrap',
        padding: '10px 12px',
        borderColor: 'var(--warning)',
        background: 'var(--warning-tint)'
      }}
    >
      <i className="pi pi-link" style={{ color: 'var(--warning)' }} />
      <span style={{ fontSize: 13, flex: 1, minWidth: 220 }}>{t.cnServeGoelett}</span>
      {onApriGoelett && (
        <button type="button" className="nbtn nbtn--ghost nbtn--sm" onClick={onApriGoelett}>
          {t.cnConfiguraGoelett}
        </button>
      )}
    </div>
  )

  const dialog = (
    <DialogConferma
      visible={conferma}
      onHide={() => setConferma(false)}
      titolo={t.cnConfermaScrittureTitolo}
      descrizione={t.cnSoloNuoveChat}
      avviso={
        scritture
          ? formatta(t.cnConfermaScritture, {
              modalita: testoModalita(modalitaConScritture, overview.modes, t).label,
              sistema: scritture.sistema,
              destinazione: scritture.destinazione
            })
          : undefined
      }
      etichettaConferma={t.cnSalvaEUsa}
      inCorso={inCorso}
      onConferma={salva}
    />
  )

  if (variante === 'community') {
    return (
      <div className="nsec" style={{ marginTop: 8 }}>
        <div className="nsec__body" style={{ display: 'grid', gap: 12 }}>
          <h4 style={{ margin: 0 }}>{t.cnMotore}</h4>
          {motore}
          {sceltaViaggi}
          {multiTratta}
          {avvisoGoelett}
          <h4 style={{ margin: 0 }}>{t.cnCanali}</h4>
          {canali}
          <h4 style={{ margin: 0 }}>{t.cnAggiunte}</h4>
          {capabilities}
          {barraSalvataggio}
        </div>
        {dialog}
      </div>
    )
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <section className="ncard" aria-labelledby="cn-titolo-modalita">
        <div className="ncard__head">
          <div className="ncard__ico">
            <i className="pi pi-sitemap" style={{ fontSize: 16 }} />
          </div>
          <div>
            <h3 className="ncard__title" id="cn-titolo-modalita">
              {t.cnModalitaPredefinita}
            </h3>
            <span style={{ fontSize: 12, color: 'var(--ink-500)' }}>
              {t.cnModalitaPredefinitaHint}
            </span>
          </div>
        </div>
        <div className="ncard__body" style={{ display: 'grid', gap: 12 }}>
          {card ? (
            <StatoCard card={card} />
          ) : (
            <span style={{ fontSize: 13, color: 'var(--ink-500)' }}>
              {t.cnNessunaConfigurazioneAgentica}
            </span>
          )}
          {motore}
          {sceltaViaggi}
          {multiTratta}
          {avvisoGoelett}
          <h4 style={{ margin: '8px 0 0' }}>{t.cnCanali}</h4>
          {canali}
        </div>
      </section>

      <section className="ncard" aria-labelledby="cn-titolo-capabilities">
        <div className="ncard__head">
          <div className="ncard__ico">
            <i className="pi pi-th-large" style={{ fontSize: 16 }} />
          </div>
          <div>
            <h3 className="ncard__title" id="cn-titolo-capabilities">
              {t.cnAggiunte}
            </h3>
            <span style={{ fontSize: 12, color: 'var(--ink-500)' }}>{t.cnCapabilitiesHint}</span>
          </div>
        </div>
        <div className="ncard__body" style={{ display: 'grid', gap: 12 }}>
          {capabilities}
          {barraSalvataggio}
        </div>
      </section>
      {dialog}
    </div>
  )
}

export default SchedaConfigurazione
